import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Components
import Sidebar from "@/components/Sidebar";
import Dashboard from "@/components/Dashboard";
import FileUpload from "@/components/FileUpload";
import ProfitLoss from "@/components/ProfitLoss";
import BalanceSheet from "@/components/BalanceSheet";
import CashFlow from "@/components/CashFlow";
import PartySales from "@/components/PartySales";
import PartyPurchase from "@/components/PartyPurchase";
import ItemSales from "@/components/ItemSales";
import ItemPurchase from "@/components/ItemPurchase";
import StockMovement from "@/components/StockMovement";
import PartyExpense from "@/components/PartyExpense";
import TrialBalance from "@/components/TrialBalance";
import MonthlyClosings from "@/components/MonthlyClosings";
import GLDump from "@/components/GLDump";
import ClosingStock from "@/components/ClosingStock";
import ExclusionSettings from "@/components/ExclusionSettings";
import MISReport from "@/components/MISReport";
import MISMappings from "@/components/MISMappings";
import CompanyManager from "@/components/CompanyManager";
import LedgerAudit from "@/components/LedgerAudit";
import Users from "@/components/Users";
import MonthLocks from "@/components/MonthLocks";
import AuditJVs from "@/components/AuditJVs";
import AuditLog from "@/components/AuditLog";
import Login from "@/components/Login";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppShell() {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState("dashboard");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("default");
  const [selectedBranch, setSelectedBranch] = useState(null);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);
      if (res.data.length === 0) {
        await axios.post(`${API}/migrate-default-company`);
        const res2 = await axios.get(`${API}/companies`);
        setCompanies(res2.data);
      }
    } catch (e) {
      console.error("Error fetching companies:", e);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const params = { company_id: selectedCompany };
      if (selectedBranch) params.branch_id = selectedBranch;
      const response = await axios.get(`${API}/reports/summary`, { params });
      setSummary(response.data);
    } catch (e) {
      console.error("Error fetching summary:", e);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, selectedBranch]);

  useEffect(() => {
    if (user && user !== false) fetchCompanies();
  }, [user, fetchCompanies]);

  useEffect(() => {
    if (user && user !== false) fetchSummary();
  }, [user, fetchSummary]);

  // Auth checking state
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-950 text-zinc-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (user === false) return <Login />;

  const handleUploadSuccess = () => {
    fetchSummary();
    toast.success("File uploaded successfully!");
  };

  const companyContext = {
    companyId: selectedCompany,
    branchId: selectedBranch,
    companies,
  };

  const renderContent = () => {
    switch (activeView) {
      case "dashboard":
        return <Dashboard summary={summary} loading={loading} onNavigate={setActiveView} onRefresh={fetchSummary} companyContext={companyContext} />;
      case "upload":
        return <FileUpload onUploadSuccess={handleUploadSuccess} companyContext={companyContext} />;
      case "profit-loss":
        return <ProfitLoss companyContext={companyContext} />;
      case "balance-sheet":
        return <BalanceSheet companyContext={companyContext} />;
      case "cash-flow":
        return <CashFlow companyContext={companyContext} />;
      case "party-sales":
        return <PartySales companyContext={companyContext} />;
      case "party-purchase":
        return <PartyPurchase companyContext={companyContext} />;
      case "item-sales":
        return <ItemSales companyContext={companyContext} />;
      case "item-purchase":
        return <ItemPurchase companyContext={companyContext} />;
      case "stock-movement":
        return <StockMovement companyContext={companyContext} />;
      case "party-expense":
        return <PartyExpense companyContext={companyContext} />;
      case "trial-balance":
        return <TrialBalance companyContext={companyContext} />;
      case "monthly-closings":
        return <MonthlyClosings companyContext={companyContext} />;
      case "gl-dump":
        return <GLDump companyContext={companyContext} />;
      case "closing-stock":
        return <ClosingStock companyContext={companyContext} />;
      case "exclusion-settings":
        return <ExclusionSettings companyContext={companyContext} />;
      case "mis-report":
        return <MISReport companyContext={companyContext} />;
      case "mis-mappings":
        return <MISMappings companyContext={companyContext} />;
      case "company-manager":
        return <CompanyManager onCompanyChange={fetchCompanies} />;
      case "ledger-audit":
        return <LedgerAudit companyContext={companyContext} />;
      case "users":
        if (user.role !== "admin") return <div className="p-6 text-zinc-600">You do not have access to this page.</div>;
        return <Users />;
      case "month-locks":
        if (user.role !== "admin") return <div className="p-6 text-zinc-600">You do not have access to this page.</div>;
        return <MonthLocks companyContext={companyContext} />;
      case "audit-jvs":
        return <AuditJVs companyContext={companyContext} />;
      case "audit-log":
        if (user.role !== "admin") return <div className="p-6 text-zinc-600">You do not have access to this page.</div>;
        return <AuditLog />;
      default:
        return <Dashboard summary={summary} loading={loading} onNavigate={setActiveView} onRefresh={fetchSummary} companyContext={companyContext} />;
    }
  };

  const currentCompany = companies.find(c => c.company_id === selectedCompany);
  const branchOptions = currentCompany?.branches || [];

  return (
    <div className="flex h-screen bg-white" data-testid="app-container">
      <Sidebar
        activeView={activeView}
        onNavigate={setActiveView}
        companies={companies}
        selectedCompany={selectedCompany}
        onCompanyChange={(id) => { setSelectedCompany(id); setSelectedBranch(null); }}
        branchOptions={branchOptions}
        selectedBranch={selectedBranch}
        onBranchChange={setSelectedBranch}
        onClientsRefresh={fetchCompanies}
      />
      <main className="flex-1 overflow-auto">
        <div className="p-6">{renderContent()}</div>
      </main>
      <ChangePasswordDialog open={!!user.must_change_password} onOpenChange={() => { /* forced */ }} forced />
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
